import React, { useMemo, useState } from "react";
import { renderTable } from "../../../../components/PaginatedListTableRenderer";
import { ListRendererProps } from "../../../../components/PaginatedList";
import { RVModelOrderRow } from "./GetRVModelOrders";
import { Row, RowProps, showGroupColumn } from "./Row";
import {
  Sort as SortOf,
  SortHeader,
  compareText,
  nextSort,
} from "../../../../components/SortHeader";

const TableRenderer = renderTable(Row);

export type SortKey =
  | "name"
  | "desc"
  | "serialNumber"
  | "orderDate"
  | "provisioned"
  | "hidden"
  | "group"
  | "owner"
  | "id";

export type Sort = SortOf<SortKey>;

const textOf = (
  o: RVModelOrderRow,
  key: SortKey,
  groupNames?: Record<string, string>
): string => {
  switch (key) {
    case "desc":
      return o.desc || "";
    case "serialNumber":
      return o.serialNumber || "";
    case "group":
      return groupNames?.[o.userGroupID || ""] || "";
    case "owner":
      return o.owner?.email || "";
    case "id":
      return o.docID;
    default:
      return o.name || "";
  }
};

// compares two orders by the sorted column alone; an empty value sorts last
// whichever the direction
const comparePrimary = (
  a: RVModelOrderRow,
  b: RVModelOrderRow,
  { key, asc }: Sort,
  groupNames?: Record<string, string>
): number => {
  const dir = asc ? 1 : -1;
  if ("orderDate" === key) return dir * (a.orderDate - b.orderDate);
  if ("provisioned" === key) {
    return dir * (Number(a.provisioned) - Number(b.provisioned));
  }
  if ("hidden" === key) {
    return dir * (Number(!!a.hidden) - Number(!!b.hidden));
  }
  const ta = textOf(a, key, groupNames);
  const tb = textOf(b, key, groupNames);
  if ("" === ta || "" === tb) return Number("" === ta) - Number("" === tb);
  return dir * compareText(ta, tb);
};

// the indices of the given orders in sorted order; ties go by name then ID,
// always ascending, so the order is stable
export const sortOrders = (
  list: RVModelOrderRow[],
  sort: Sort,
  groupNames?: Record<string, string>
): number[] =>
  list
    .map((_, i) => i)
    .sort(
      (i, j) =>
        comparePrimary(list[i], list[j], sort, groupNames) ||
        compareText(list[i].name || "", list[j].name || "") ||
        compareText(list[i].docID, list[j].docID)
    );

export const MyTableRenderer = (
  props: ListRendererProps<RVModelOrderRow> & RowProps
): JSX.Element => {
  const { list, onDeleted, ...rest } = props;
  const [sort, setSort] = useState<Sort>({ key: "name", asc: true });
  const order = useMemo(() => sortOrders(list || [], sort, props.groupNames), [
    list,
    sort,
    props.groupNames,
  ]);
  const sorted = useMemo(() => order.map((i) => list[i]), [order, list]);

  const handleSort = (key: SortKey): void => setSort((s) => nextSort(s, key));
  const header = (label: string, key: SortKey): JSX.Element => (
    <SortHeader label={label} sortKey={key} sort={sort} onSort={handleSort} />
  );

  return (
    <TableRenderer
      {...rest}
      list={sorted}
      // a row reports its displayed index; the list holds them unsorted
      onDeleted={(i: number): void => onDeleted(order[i])}
    >
      {header("Name", "name")}
      {header("Description", "desc")}
      {header("Serial #", "serialNumber")}
      {header("Order date", "orderDate")}
      {header("Provisioned", "provisioned")}
      {header("Hidden", "hidden")}
      {showGroupColumn(props.groupNames) && header("Group", "group")}
      {props.showOwner && header("Owner", "owner")}
      {props.displayIDs && header("ID", "id")}
      <div></div>
    </TableRenderer>
  );
};
